import {
  Box,
  FormField,
  Input,
  SpaceBetween,
  Header,
  Table,
  Button
} from "@cloudscape-design/components";
import { useParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import SubmitButton from "styles/SubmitButton";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import organizationService from "services/organizationService";
import "../styles/common.css";
import { useNotification } from "contexts/NotificationContext";
import {  DomainProp, OrganizationDetails } from "types/organizationTypes";
import UserService from "services/UserService";
import PERMISSIONS from "configs/Permissions";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";
import DomainsConfigModal from "components/organizations/DomainsConfigModal";
import { validateDomain } from "utils/validationUtils";
import { handleDownload } from "utils/Utils";
import axios from "axios";
import AppConfig from "configs/AppConfig";


export default function DomainSetup() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { addNotification, removeNotification, addLoaderNotification } = useNotification();
  const { id } = useParams();
  const location = useLocation();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(location.state?.organization || null);
  const [domainName, setDomainName] = useState("");
  const [domainsList, setDomainsList] = useState<DomainProp[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<DomainProp | null>(null);
  
  // Loading
  const [loadingDomains, setLoadingDomains] = useState<boolean>(false);
  const [isAddingDomain, setIsAddingDomain] = useState<boolean>(false);
  const [isDeletingDomain, setIsDeletingDomain] = useState<boolean>(false);
  
  // Error
  const [errorLoadingDomains, setErrorLoadingDomains] = useState<string | null>(null);
  const [errorInDomainField, setErrorInDomainField] = useState<string | null>(null);

  // Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);


  // Fetch Organization
  const fetchOrganizationData = async () => {
    if (!id) return;
    try {
      const orgData = await organizationService.getOrganizationById(id);
      setOrganization(orgData);
    } catch (error) {
      console.error("Error fetching organization:", error);
      addNotification("error", "Failed to fetch organization's display name");
    }
  };

  // Fetch Domains
  const fetchDomains = async () => {
    if (!id) return;
    try {
      setLoadingDomains(true);
      const domains = await organizationService.fetchDomains(id);
      setDomainsList(domains);
    } catch (error) {
      console.error("Error fetching domains:", error);
      setErrorLoadingDomains("Failed to load domains");
    } finally {
      setLoadingDomains(false);
    }
  };

  // Add Domain
  const handleAddDomain = async() =>{
    if (!id) return;

    // Structural validation: domain must contain at least one dot
    if(!domainName.includes(".")){
      setErrorInDomainField("Invalid domain: must contain at least one dot.");
      return;
    }

    try {
      setIsAddingDomain(true);
      await organizationService.addNewDomain(id, domainName)
      addNotification("success", "New domain is added successfully.");
      setDomainName("");
      fetchDomains();
    } catch (error) {

      if(axios.isAxiosError(error)){
        if(error?.response?.data?.error === "DOMAIN_ALREADY_EXISTS"){
          setErrorInDomainField("Domain name already exists");
  
        }else{
          addNotification("error", "Failed to add new domain");
        }
      }
      
      console.error("Error adding new domain:", error);
    } finally{
      setIsAddingDomain(false);
    }
  }
  
  // verify Domain
  const handleVerifyDomain = async(name: string, nameServers: string[]) =>{
    if (!id) return;
    try {
      addLoaderNotification("Verifying...");
      const data = await organizationService.verifyDomain(id, name, nameServers)
      removeNotification("all");
      if(data.isValid) {
        addNotification("success", "Domain is verified successfully");
        fetchDomains();
      }else{
        addNotification("error", "Please re-check your configuration, domain is still not verified");
      }

    } catch (error) {
      console.error("Problem occured, couldn't verify domain:", error);
      removeNotification("all");
      addNotification("error", "Problem occured, couldn't verify domain");
    }
  }

  // Delete Domain
  const handleDeleteDomain = async() =>{
    if (!id || !selectedDomain) return;
    try {
      setIsDeletingDomain(true);
      await organizationService.deleteDomain(id, selectedDomain?.name, selectedDomain?.hosted_zone_id)
      addNotification("success", "Domain is deleted successfully");
      setSelectedDomain(null);
      fetchDomains();
    } catch (error) {
      console.error("Error deleting domain:", error);
      addNotification("error", "Failed to delete domain");
    }finally {
      setIsDeletingDomain(false);
      setShowDeleteModal(false)
    }
  }


  useEffect(() => {
    fetchDomains();
    
    if(!organization){
      fetchOrganizationData();
    }
    
  }, []);

  useEffect(() => {
    if (organization) {
      setBreadcrumbs([
        { text: AppConfig.APP_NAME, href: "/" },
        { text: "Organizations", href: "/organizations" },
        { text: organization.display_name, href: `/organizations/${id}` },
        { text: "Settings", href: `/organizations/${id}/settings` },
        { text: "Domains", href: `/organizations/${id}/domains` },
      ]);
    }

    // Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [organization, id, setBreadcrumbs]);


  return (
    <SpaceBetween size="l">
      <Header variant="h2"
        info={<Button variant="icon" iconName="refresh" onClick={fetchDomains} disabled={loadingDomains} />}
      >
        Domains
      </Header>

      <Table
          items={domainsList}
          columnDefinitions={[
            {
              id: "name",
              header: "Domain name",
              cell: (item: DomainProp) => (
                <div className="action-column-content">
                  <span>{item.name}</span> 
                  {!item.is_verified && <Button 
                    onClick={()=>{
                      setSelectedDomain(item);
                      setShowConfigModal(true);
                    }} 
                    iconName="copy"
                    variant="icon"
                  />}
                </div>
              ),
            },
            {
              id: "status",
              header: "Status",
              cell: (item: DomainProp) =>
                item.is_used ? (
                  <strong className="neutral-field">In use</strong>
                ) : item.is_verified ? (
                  <strong className="verified-field">Verified</strong>
                ) : (
                  <strong className="pending-field">Unverified</strong>
                ),
            },
            {
              id: "action",
              header: "Actions",
              cell: (item: DomainProp) => (
                <div className="action-column-content">
                  <Button 
                    variant="link"
                    onClick={()=>{
                      handleVerifyDomain(item.name, item.name_servers);
                    }}
                    disabled={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])}>
                      Verify domain
                  </Button>

                  <Button
                    iconName="download"
                    variant="icon"
                    onClick={() => {handleDownload(`${item.name}-configs.txt`, item.name_servers)}}
                  />

                  {UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS]) && (
                    <Button 
                      variant="primary" 
                      className="button-unstyled" 
                      disabled={item.is_used} 
                      disabledReason="In use by a deployment!"
                    >
                      <Button
                        className={`icon-button-no-padding ${!item.is_used ? "required-field" : ""}`}
                        disabled={item.is_used}
                        iconName="remove"
                        variant="icon"
                        onClick={() => {
                          setSelectedDomain(item);
                          setShowDeleteModal(true);
                        }}
                      />
                    </Button>
                  )}
                </div>
              ),
              minWidth: 250,
            },
          ]}
          loading={loadingDomains}
          loadingText="Loading organization domains..."
          empty={
            <Box textAlign="center" color="inherit">
              {errorLoadingDomains ? (
                <>
                  <b>Error</b>
                  <Box padding={{ bottom: "s" }} variant="p">
                    {errorLoadingDomains}
                  </Box>
                </>
              ) : (
                <>
                  <b>No domains</b>
                  <Box padding={{ bottom: "s" }} variant="p">
                    No domains to display, please create one.
                  </Box>
                </>
              )}
            </Box>
          }
          stripedRows
          stickyHeader
        />
        
      <SpaceBetween size="l">
        <FormField
          className="same-width-container"
          label="Domain name"
          constraintText={<>Domain name must be unique and has maximum 50 characters. Character count: {domainName.length}/50</>}
          errorText={errorInDomainField}
        >
          <Input
            value={domainName}
            placeholder="www.your-domain.com"
            onChange={({ detail }) => {
              setDomainName(detail.value)
              const result = validateDomain(detail.value);
              setErrorInDomainField(result.error);
            }}
          />
        </FormField>

        <Box textAlign="right">
          <div className="update-button">
            <SubmitButton
              onClick={handleAddDomain}
              variant="primary"
              loading={isAddingDomain}
              disabled={!validateDomain(domainName).isValid || !!errorInDomainField}
            >
              Add domain
            </SubmitButton>
          </div>
        </Box>
      </SpaceBetween>

      <DeleteConfirmationModal
        isVisible={showDeleteModal}
        onDismiss={() => {
          setShowDeleteModal(false);
          setSelectedDomain(null);
        }}
        onConfirm={handleDeleteDomain}
        isDeleting={isDeletingDomain}
        headerText="Delete Domain"
        deleteConfirmationValue="delete domain"
      >
        Are you sure you want to delete domain <b>{selectedDomain?.name}</b>?
      </DeleteConfirmationModal>

      <DomainsConfigModal
        domainName={selectedDomain?.name ?? ""}
        nameServers={selectedDomain?.name_servers ?? []}
        isVisible={showConfigModal}
        onDismiss={()=>{
          setShowConfigModal(false);
          setSelectedDomain(null);
        }}
      />
    </SpaceBetween>
  );
}
